<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import { useAuthStore } from "@/stores/auth.module";
import user from "@/api/user";
import Button from "@/stories/buttons/Button/Button.vue";
import LoadingDialog from "@/stories/dialogs/LoadingDialog.vue";
import IBGLogoBlackSmall from "@/assets/svg/ibg-logo-black-small.svg?component";
import IBGTitle from "@/assets/svg/interaktiv-borgerguide.svg?component";
import SVG from "@/stories/displays/SVG/SVG.vue";

const authStore = useAuthStore();
const loading = ref(false);
const scrollContainer = ref<HTMLDivElement | null>(null);
const showButton = ref<boolean>(true);
const hasReachedBottom = ref<boolean>(false);
let lastScrollTop = 0;

const accept = () => {
  loading.value = true;
  user
    .save({
      id: authStore.id,
      settings: {
        policies: {
          managerPolicyAccepted: true,
        },
      },
    })
    .then(() => {
      authStore.$patch({ privacyPolicyAccepted: true });
    })
    .catch(() => {
      throw new Error("Could not accept privacy policy");
    })
    .finally(() => {
      loading.value = false;
    });
};

const scrollToBottom = () => {
  if (scrollContainer.value) {
    scrollContainer.value.scrollTo({
      top: scrollContainer.value.scrollHeight,
      behavior: "smooth",
    });
  }
  showButton.value = false;
  hasReachedBottom.value = true;
};

const handleScroll = () => {
  if (!scrollContainer.value) return;

  const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value;
  const bottomThreshold = 5;
  const atBottom = scrollTop + clientHeight >= scrollHeight - bottomThreshold;
  hasReachedBottom.value = true;

  const scrollingUp = scrollTop < lastScrollTop;
  lastScrollTop = scrollTop;

  if (atBottom) {
    showButton.value = false;
  } else if (hasReachedBottom.value && scrollingUp) {
    showButton.value = true;
  }
};

onMounted(() => {
  scrollContainer.value?.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
  scrollContainer.value?.removeEventListener("scroll", handleScroll);
});
</script>

<template>
  <div
    ref="scrollContainer"
    data-test="scroll-container"
    class="py-12 flex flex-col items-center bg-neutral100 overflow-y-auto h-screen"
  >
    <div class="flex">
      <IBGLogoBlackSmall class="mb-10 mr-3" />
      <IBGTitle />
    </div>
    <div
      class="bg-neutral0 p-8 desktop:max-w-[46.25rem] tablet:max-w-[37.5rem] mobile:max-w-[90%] rounded-3xl"
    >
      <p class="privacy-policy mb-8" v-html="$t('index.gdpr_new')"></p>
      <div class="w-full flex justify-center">
        <Button :text="$t('index.acceptAndContinue')" @click="accept"></Button>
      </div>
    </div>
  </div>

  <transition name="fade">
    <div
      data-test="scroll-button"
      v-if="showButton"
      class="fixed w-full bottom-7 flex justify-center"
      @click="scrollToBottom"
    >
      <div
        class="bg-neutral0 flex items-center justify-center rounded-[1rem] shadow-large p-4 border border-1 border-neutral300 tablet:w-[32.4375rem] mobile:w-[85%] text-center cursor-pointer"
      >
        <p class="b300 mr-6">{{ $t("index.scrollButton") }}</p>

        <div
          class="rounded-full w-8 h-8 border border-1 border-neutral300 shadow-small-bottom flex items-center justify-center flex-shrink-0"
        >
          <SVG
            icon="far-chevron-down"
            width="16"
            height="16"
            fill="neutral1000"
          >
          </SVG>
        </div>
      </div>
    </div>
  </transition>

  <LoadingDialog v-if="loading" />
</template>

<style lang="scss" scoped>
// We need to explicitely add these classes again because tailwind won't register them from styles.scss - ticket will be created to find a proper fix
.b300 {
  font-family: "Source Sans 3 Variable", sans-serif;
  -webkit-font-smoothing: antialiased;
  font-size: 1rem;
  line-height: 1.375rem;
  font-weight: 400;
  letter-spacing: -0.5px;
}

.h900 {
  font-family: "Source Sans 3 Variable", sans-serif;
  -webkit-font-smoothing: antialiased;
  font-size: 2.5rem;
  line-height: 2.75rem;
  font-weight: 700;
  letter-spacing: -0.4px;
}

.h800 {
  font-family: "Source Sans 3 Variable", sans-serif;
  -webkit-font-smoothing: antialiased;
  font-size: 2rem;
  line-height: 2.375rem;
  font-weight: 700;
  letter-spacing: -0.4px;
}

.shadow-large {
  box-shadow: 0 0 1.25rem 0 rgba(0, 0, 0, 0.15);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.privacy-policy {
  font-family: "Source Sans 3 Variable", sans-serif;
  @apply b300;

  h1 {
    @apply h900 mb-4;
  }

  h2,
  h3 {
    @apply h800 mb-4 mt-6;
  }

  p,
  #text {
    @apply b300 mb-2;
  }

  ol,
  ul {
    @apply ml-6;
  }

  ul {
    @apply list-[initial];

    li {
      &::marker {
        @apply text-[0.625rem];
      }
    }
  }

  ol {
    @apply list-decimal;

    li {
      @apply mb-4;

      &::marker {
        @apply font-bold;
      }
    }
  }
}
</style>
